import { useEffect, useMemo, useState } from "react";

import { Coding, QuestionnaireItem, ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Checkbox, Stack } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { isArray } from "lodash";

import { isEmptyAnswerCoding, isEmptyAnswerValue } from "@/intake-forms/utils/multi-select";
import { sortEmptyAnswerOptionsFirst } from "@/intake-forms/utils/value-set-sorting";
import { getValueSetOptions } from "@/utils/medplum";

import {
  questionnaireCheckboxOptionTestId,
  questionnaireFieldTestId,
} from "../../utils/questionnaire-field-testid";

type QuestionnaireValueSetCheckboxField<T> = {
  label?: string;
  name: string;
  form: UseFormReturnType<T>;
  group: QuestionnaireItem | undefined;
};

const QuestionnaireValueSetCheckboxField = <T,>({
  form,
  label,
  name,
  group,
}: QuestionnaireValueSetCheckboxField<T>) => {
  const { checked, onChange, ...formInputProps } = form.getInputProps(name, { type: "checkbox" });
  const [valueSetOptions, setValueSetOptions] = useState<ValueSetExpansionContains[]>([]);

  const medplum = useMedplum();
  const answerValueSet = group?.answerValueSet;

  useEffect(() => {
    const getValueSet = async () => {
      const valueSetElements = await getValueSetOptions(medplum, answerValueSet);

      if (valueSetElements?.length) {
        setValueSetOptions(sortEmptyAnswerOptionsFirst(valueSetElements));
      }
    };

    getValueSet();
  }, [answerValueSet, medplum]);

  const checkedCodeArray: string[] = useMemo(() => {
    let checkedValue = checked;

    if (checked && !isArray(checked)) checkedValue = [checked];

    return checkedValue?.map((item: { valueCoding: Coding } | string) => {
      if (typeof item === "string") return item;
      if (!item?.valueCoding) return JSON.stringify(item);

      return JSON.stringify(item.valueCoding);
    });
  }, [checked]);

  const selectedValues: string[] = checkedCodeArray || [];
  const hasEmptyAnswerSelected = selectedValues.some(isEmptyAnswerValue);

  // "Empty answers" (eg None) exclude every other answer, so the selection holds one side or the
  // other: checking an empty answer collapses the selection to it, and checking anything else drops
  // the empty answer.
  const handleChange = (values: string[]) => {
    const addedValue = values.find((value) => !selectedValues.includes(value));

    if (addedValue && isEmptyAnswerValue(addedValue)) {
      onChange([addedValue]);
      return;
    }

    onChange(values.filter((value) => !isEmptyAnswerValue(value)));
  };

  return (
    <Checkbox.Group
      label={label}
      value={selectedValues}
      onChange={handleChange}
      data-testid={questionnaireFieldTestId(name)}
      {...formInputProps}
    >
      <Stack mt="sm">
        {valueSetOptions?.map((item) => {
          const isEmptyAnswer = isEmptyAnswerCoding(item);
          // A checked empty answer stays enabled, so a response saved with both an empty answer and
          // a real one — possible before this rule existed — can be unlocked by unchecking it.
          const disabled = hasEmptyAnswerSelected
            ? !isEmptyAnswer
            : isEmptyAnswer && selectedValues.length > 0;

          return (
            <Checkbox
              key={item.code}
              disabled={disabled}
              label={item.display}
              value={JSON.stringify(item)}
              data-testid={
                item.display ? questionnaireCheckboxOptionTestId(name, item.display) : undefined
              }
            />
          );
        })}
      </Stack>
    </Checkbox.Group>
  );
};

export default QuestionnaireValueSetCheckboxField;
